import { createTheme } from '@mui/material/styles';
import { grey, purple, red } from '@mui/material/colors';

const theme = createTheme({
  breakpoints: {
    values: {
      xs: 0,
      sm: 990,
      md: 1240,
      lg: 1520,
      xl: 1920,
    },
  },
  palette: {
    background: {
      paper: 'var(--color-bg)',
    },
    primary: {
      main: '#7688b9',
      dark: '#374785',
    },
    secondary: {
      main: '#92445f',
    },
    common: {
      border1: `1px solid ${grey[500]}`,
      border2: `1px solid ${purple[500]}`,
      borderError: `1px solid ${red[700]}`,
    },
  },
});

theme.components = {
  /* font */
  MuiTypography: {
    styleOverrides: {
      root: {
        fontFamily: 'var(--main-font-family-medium)',
        fontSize: 'var(--font-size-s)',
        fontWeightRegular: 'var(--font-weight-regular)',
        fontWeightMedium: 'var(--font-weight-medium)',
        fontWeightBold: 'var(--font-weight-bold)',
      },
    },
    defaultProps: {
      variantMapping: {
        subtitle2: 'p',
      },
    },

    variants: [
      {
        props: { variant: 'h1' },
        style: { fontSize: 'var(--font-size-xl)' },
      },
      {
        props: { variant: 'h2' },
        style: { fontSize: 'var(--font-size-l)' },
      },
      {
        props: { variant: 'h5' },
        style: {
          fontSize: 'var(--font-size-m)',
        },
      },
      {
        props: { variant: 'subtitle2' },
        style: {
          fontSize: 'var(--font-size-s)',
        },
      },
    ],
  },
  /* select */
  MuiSelect: {
    styleOverrides: {
      root: {
        backgroundColor: theme.palette.background.paper,
      },
    },
  },

  /* select + input */
  MuiInput: {
    styleOverrides: {
      root: {
        '&:after, &:hover&:before': {
          borderBottom: theme.palette.common.border1,
        },
        '&.Mui-focused&:before,&:after': {
          borderBottom: theme.palette.common.border2,
        },
        '&.Mui-focused&:after': {
          boxShadow: 'var(--box-shadow-primary)',
        },
      },
    },
  },
  MuiOutlinedInput: {
    styleOverrides: {
      root: {
        padding: 0,
        fontSize: 'inherit',
        border: theme.palette.common.border1,
        borderRadius: 'var(--border-radius-s)',
        backgroundColor: grey[100],
        '.MuiTableCell-root & .MuiOutlinedInput-input': {
          padding: '3px',
        },
        '&.column-filter .MuiOutlinedInput-input': {
          padding: '3px 0px 3px 7px',
        },
        '& .MuiOutlinedInput-input': {
          padding: '8.5px 24px 8.5px 14px',
        },
        '&.Mui-error, &.Mui-error&.Mui-focused': {
          border: theme.palette.common.borderError,
        },

        '& fieldset': {
          border: 'none',
        },
        '&.Mui-focused': {
          borderColor: theme.palette.primary.light,
          boxShadow: 'var(--box-shadow-primary)',
        },
        '.MuiTableCell-root &.Mui-focused': {
          boxShadow: 'none',
        },
        '&.Mui-focused .MuiOutlinedInput-input': {
          backgroundColor: 'transparent',
        },
        '&.MuiInputBase-adornedEnd': {
          paddingRight: '3px',
        },
      },
    },
  },
  MuiInputLabel: {
    styleOverrides: {
      root: {
        top: -45,
        '&.Mui-focused': {
          color: 'var(--color-text-gray)',
        },
      },
    },
  },

  MuiInputAdornment: {
    styleOverrides: {
      root: {
        '& .MuiSvgIcon-root': {
          color: grey[400],
        },
      },
    },
  },

  MuiAutocomplete: {
    styleOverrides: {
      root: {
        '& .MuiOutlinedInput-root': {
          padding: '8.5px 24px 8.5px 14px',
        },
        '.MuiAutocomplete-inputRoot .MuiAutocomplete-input': {
          padding: 0,
        },
        '& .MuiAutocomplete-popupIndicator:hover': {
          backgroundColor: 'inherit',
        },
        '& .MuiTouchRipple-root': {
          display: 'none',
        },
      },
    },
  },
  MuiPaper: {
    styleOverrides: {
      root: {
        '&.MuiAutocomplete-paper': {
          boxShadow: 'var(--box-shadow-paper)',
        },
        '& .MuiAutocomplete-listbox, & .MuiMenu-list': {
          maxHeight: 'var(--dropdown-menu-max-height)',
        },
        '& .MuiAutocomplete-listbox .MuiAutocomplete-option': {
          padding: '5px 10px',
          height: '3rem',
        },
        '&.textfield': {
          minHeight: '40px',
          maxWidth: '100%',
          padding: '8.5px 14px',
          border: theme.palette.common.border1,
          lineHeight: 1.5,
          borderRadius: 'var(--border-radius-s)',
          backgroundColor: grey[100],
          whiteSpace: 'pre-line',
        },
      },
    },
  },

  /* list */
  MuiList: {
    styleOverrides: {
      root: {
        padding: 0,
        '&.marked': {
          listStyleType: 'disc',
          paddingLeft: '1rem',
        },
      },
    },
  },
  MuiListItem: {
    styleOverrides: {
      root: {
        padding: 0,
        '.marked &': {
          display: 'list-item',
        },
      },
    },
  },
  MuiListItemText: {
    styleOverrides: {
      root: {
        margin: 0,
      },
    },
  },

  /* menu */
  MuiMenu: {
    variants: [
      {
        props: { size: 'small' },
        style: {
          width: 220,
        },
      },
    ],
  },
  MuiMenuItem: {
    styleOverrides: {
      root: {
        padding: '5px 10px',
        height: '3rem',
        '& .MuiTouchRipple-root': {
          display: 'none',
        },
        '& .MuiTypography-root': {
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
        },
      },
    },
  },

  /* table */
  MuiTableContainer: {
    styleOverrides: {
      root: {
        overflowX: 'initial',
      },
    },
  },
  MuiTableHead: {
    styleOverrides: {
      root: {
        backgroundColor: 'var(--color-green-80)',
        zIndex: 20,
        '& tr': {
          borderTop: 'none',
          boxShadow: 'var(--box-shadow-row-bottom)',
        },
        '.sticky &': {
          position: 'sticky',
          top: '0px',
          // [theme.breakpoints.up('sm')]: {
          //   top: '64px',
          // },
        },
        '.sticky.with-tabs &': {
          top: '98px',
          [theme.breakpoints.up('sm')]: {
            top: '110px',
          },
        },
      },
    },
  },
  MuiTableCell: {
    styleOverrides: {
      root: {
        padding: '7px 10px',
        textAlign: 'left',
        border: theme.palette.common.border1,
        whiteSpace: 'pre-line',
        '&.center': {
          textAlign: 'center',
        },
      },
      head: {
        fontWeight: 'var(--font-weight-bold)',
        textAlign: 'center',
      },
      body: {
        position: 'relative',
        overflowWrap: 'break-word',
      },
    },
  },

  /* boxes */
  MuiFormControl: {
    styleOverrides: {
      root: {
        '.MuiTableCell-root &': {
          width: '100%',
        },
      },
    },
    variants: [
      {
        props: { size: 'small' },
        style: { margin: '1.5rem 0.5rem 0.5rem', width: 220 },
      },
    ],
  },

  // tabs
  MuiTabs: {
    styleOverrides: {
      root: {
        position: 'sticky',
        top: '50px',
        zIndex: 100,
        backgroundColor: 'var(--color-bg)',
        [theme.breakpoints.up('sm')]: {
          top: '64px',
        },
      },
    },
  },

  /* button */
  MuiButtonGroup: {
    styleOverrides: {
      root: {
        '&.edit-cell-btn .MuiButtonGroup-grouped': {
          minWidth: 'fit-content',
        },
      },
    },
  },
  MuiButton: {
    styleOverrides: {
      root: {
        border: 'none',
        borderRadius: 'var(--border-radius-s)',
        padding: '7px 21px',
        '&.MuiButton-outlinedPrimary:hover': {
          backgroundColor: 'var(--color-gray)',
          border: `1px solid ${theme.palette.secondary.light}`,
          boxShadow: 'var(--box-shadow-button)',
        },
      },
    },
    variants: [
      {
        props: { color: 'secondary' },
        style: {
          backgroundColor: theme.palette.secondary.main,
          border: 'none',
        },
      },
    ],
  },
  MuiIconButton: {
    styleOverrides: {
      root: {
        '&.MuiAutocomplete-popupIndicator': {
          transition: 'transform 0.2s ease-in 0s',
        },
        '&.MuiAutocomplete-popupIndicatorOpen': {
          color: theme.palette.secondary.main,
          transform: 'rotate(180deg)',
          transition: 'transform 0.2s ease-in 0s',
        },
      },
    },
    variants: [
      {
        props: { size: 'sm' },
        style: {
          padding: '2px',
        },
      },
      {
        props: { variant: 'outlined' },
        style: {
          border: 'none',
          outline: '1px solid var(--color-gray)',
          '&:hover': {
            boxShadow: 'var(--box-shadow-light)',
          },
        },
      },
    ],
  },

  // link
  MuiLink: {
    styleOverrides: {
      root: {
        width: 'fit-content',
        color: theme.palette.primary.dark,
        '&:visited': {
          color: theme.palette.primary.dark,
        },
      },
    },
  },

  // checkbox
  MuiCheckbox: {
    styleOverrides: {
      root: {
        '&.column-filter-checkbox': {
          padding: '3px',
        },
      },
    },
  },

  /* icons */
  MuiSvgIcon: {
    styleOverrides: {
      root: {
        '&.MuiSelect-icon': {
          transition: 'transform 0.2s ease-in 0s',
        },

        '&.MuiSelect-iconOpen': {
          color: theme.palette.primary.main,
          transform: 'rotate(180deg)',
          transition: 'transform 0.2s ease-in 0s',
        },
      },
    },
  },
};

export default theme;
